import { styled, Box, Typography } from '@mui/material'

import BoltIcon from '@mui/icons-material/Bolt'
import { getAlertColors } from '@/utils/statusColors.ts'
import { useTheme } from '@mui/material/styles'
import type { StatusVariant } from '@/types/status.types'

const Container = styled(Box)(({ theme }) => ({
  display: 'flex',
  position: 'relative', // so the progress bar is aligned to the container

  justifyContent: 'space-between',
  alignItems: 'center',
  minHeight: 20,

  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.default,

  [`& > *`]: {
    padding: theme.spacing(0.5, 0.5),
    zIndex: 100
  }
}))

const ProgressBar = styled(Box)(({ theme }) => ({
  position: 'absolute',
  backgroundColor: theme.palette.success.main,
  borderWidth: '1px',
  borderRadius: theme.shape.borderRadius,
  height: '100%',
  zIndex: 0
}))

export interface ChargeBarProps {
  chargePercentage: number
  range?: number
  maximumRange?: number
}

function determineSeverity(percentage: number): StatusVariant {
  if (percentage < 30) return 'error'
  else if (percentage < 60) return 'warning'
  else return 'success'
}

export function ChargeBar({ chargePercentage, range }: ChargeBarProps) {
  if (chargePercentage < 0) chargePercentage = 0.0
  if (chargePercentage > 1) chargePercentage = 1.0

  chargePercentage = Math.round(chargePercentage * 100)

  const theme = useTheme()
  const colors = getAlertColors(determineSeverity(chargePercentage), theme)

  return (
    <Container>
      <ProgressBar
        sx={{
          width: `${chargePercentage}%`,
          backgroundColor: colors.backgroundColor,
          borderColor: colors.borderColor
        }}
      ></ProgressBar>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <BoltIcon sx={{ fontSize: 12, color: colors.iconColor }} />
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: colors.borderColor }}>
          {chargePercentage}%
        </Typography>
      </Box>
      {range && <Typography variant="caption">{range}kms</Typography>}
    </Container>
  )
}
